import React, { useMemo } from "react";
import {
    ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList
} from "recharts";

type BackendItem = {
    headquarterId: string;
    headquarterName: string;
    totalOilCollectedKg: string | number;
};

type Props = {
    title?: string;
    periodLabel: string;
    items?: BackendItem[];     // datos tal cual vienen del backend
    loading?: boolean;
    error?: string | null;
    height?: number;           // px (default 320)
    className?: string;
    color?: string;            // color de barra (por defecto el verde del dashboard)
    maxRows?: number;          // para recortar el “top N”, default 6
};

const fmtKg = (n: number) =>
    `${new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n)} kg`;

export const OilCollectionRanking: React.FC<Props> = ({
                                                          title = "Ranking por sede — aceite recolectado",
                                                          periodLabel,
                                                          items = [],
                                                          loading = false,
                                                          error = null,
                                                          height = 320,
                                                          className = "",
                                                          color = "#d67611",
                                                          maxRows = 6,
                                                      }) => {
    const data = useMemo(() => {
        const parsed = (items || []).map(it => ({
            id: it.headquarterId,
            name: it.headquarterName,
            value: Number.parseFloat(String(it.totalOilCollectedKg)) || 0,
        }));
        // ordena DESC y corta top N
        return parsed.sort((a, b) => b.value - a.value).slice(0, maxRows);
    }, [items, maxRows]);

    const noData = !loading && !error && data.every(d => d.value === 0) && data.length === 0;

    return (
        <div className={`rounded-2xl border bg-white shadow-sm ${className}`} style={{ minHeight: height }}>
            {/* Header */}
            <div className="p-4 flex items-center justify-between">
                <h3 className="font-semibold">{title}</h3>
                <div className="text-sm text-gray-600">{periodLabel}</div>
            </div>

            <div className="px-4 pb-6 relative">
                {loading ? (
                    <div className="p-6 text-sm text-gray-500">Cargando ranking…</div>
                ) : error ? (
                    <div className="p-6 text-sm text-rose-600">{error}</div>
                ) : data.length === 0 ? (
                    <>
                        <div className="p-6 text-sm text-gray-500 opacity-40 blur-[2px] select-none">
                            (No hay elementos para mostrar)
                        </div>
                        <div className="absolute inset-0 z-10 flex items-center justify-center">
                            <div className="rounded-xl bg-white/80 px-4 py-2 text-sm text-gray-700 shadow">
                                No hay datos para el periodo seleccionado.
                            </div>
                        </div>
                    </>
                ) : (
                    <div style={{ height: height - 90 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data}
                                layout="vertical"
                                margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                                barCategoryGap={12}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    type="number"
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(v) => new Intl.NumberFormat("es-ES").format(Number(v))}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={140}
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 12 }}
                                />
                                <Tooltip
                                    cursor={{ opacity: 0.08 }}
                                    formatter={(v: any) => [fmtKg(Number(v)), "Aceite recolectado"]}
                                />
                                <Bar dataKey="value" radius={[8, 8, 8, 8]} fill={color}>
                                    {/* etiqueta a la derecha de cada barra */}
                                    <LabelList
                                        dataKey="value"
                                        position="right"
                                        formatter={(v: any) => fmtKg(Number(v))}
                                        className="text-[11px] fill-gray-700"
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};
